import { useCallback, useEffect, useState } from "react";
import { communityService } from "../features/community/service";
import type { CommunityComment, CommunityPost } from "../features/community/types";
import { formatIST } from "../utils/dateUtils";

const TABS: Array<{ key: string; label: string }> = [
  { key: "all", label: "All" },
  { key: "poll", label: "Polls" },
  { key: "question", label: "Q&A" },
  { key: "announcement", label: "Announcements" },
];

const isImage = (url: string) => /\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(url);
const isVideo = (url: string) => /\.(mp4|webm|mov)(\?|$)/i.test(url);

const typeBadge = (t: string) => {
  const map: Record<string, string> = {
    poll: "badge-brand",
    question: "badge-warning",
    announcement: "badge-success",
  };
  return map[t] || "badge-neutral";
};

// ── Poll ────────────────────────────────────────────────────────────────────

const PollCard: React.FC<{ post: CommunityPost; onChange: (p: CommunityPost) => void }> = ({
  post,
  onChange,
}) => {
  const [voting, setVoting] = useState<number | null>(null);
  const [error, setError] = useState("");
  const options = post.poll_options || [];
  const total = post.total_votes ?? options.reduce((s, o) => s + (o.votes || 0), 0);
  const showResults = post.user_has_voted || post.status !== "open";

  const vote = async (i: number) => {
    setVoting(i);
    setError("");
    try {
      onChange(await communityService.vote(post.id, i));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not record your vote.");
    } finally {
      setVoting(null);
    }
  };

  return (
    <div className="space-y-2.5">
      {options.map((opt, i) => {
        const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
        if (showResults) {
          return (
            <div key={i}>
              <div className="mb-1 flex justify-between text-sm">
                <span className="text-foreground">{opt.text}</span>
                <span className="text-foreground-muted">
                  {opt.votes} · {pct}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-surface-subtle">
                <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        }
        return (
          <button
            key={i}
            type="button"
            disabled={voting !== null || post.status !== "open"}
            onClick={() => vote(i)}
            className="btn-secondary w-full justify-start"
          >
            <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-xs">
              {String.fromCharCode(65 + i)}
            </span>
            {opt.text}
            {voting === i && <span className="spinner spinner-sm ml-auto" />}
          </button>
        );
      })}
      <p className="text-xs text-foreground-muted">
        {total} vote{total === 1 ? "" : "s"}
        {post.user_has_voted && " · ✓ You voted"}
      </p>
      {error && <p className="alert-error">{error}</p>}
    </div>
  );
};

// ── Question ──────────────────────────────────────────────────────────────────

const QuestionCard: React.FC<{ post: CommunityPost; onChange: (p: CommunityPost) => void }> = ({
  post,
  onChange,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState<CommunityComment[] | null>(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      setComments(await communityService.listComments(post.id));
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [post.id]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && comments === null) loadComments();
  };

  const submit = async () => {
    if (!draft.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const created = await communityService.answer(post.id, draft.trim());
      setComments((prev) => [...(prev || []), created]);
      setDraft("");
      onChange({
        ...post,
        user_has_responded: true,
        comment_count: (post.comment_count || 0) + 1,
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not submit your answer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <button type="button" onClick={toggle} className="text-sm font-medium text-brand">
        {expanded ? "Hide answers" : `View answers (${post.comment_count ?? 0})`}
      </button>

      {expanded && (
        <div className="space-y-3">
          {loadingComments ? (
            <p className="text-sm text-foreground-muted">Loading answers…</p>
          ) : comments && comments.length > 0 ? (
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c.id} className="rounded-xl border border-border bg-surface-subtle p-3">
                  <p className="text-xs text-foreground-muted">
                    {c.author_name} · {c.author_role}
                    {c.is_accepted && <span className="badge-success ml-2">Accepted</span>}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{c.body}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-muted">No answers yet — be the first.</p>
          )}

          <div className="space-y-2">
            <textarea
              className="textarea"
              rows={2}
              placeholder="Write your answer…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            {error && <p className="alert-error">{error}</p>}
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !draft.trim()}
              className="btn-primary btn-sm"
            >
              {submitting ? "Submitting…" : "Submit answer"}
            </button>
          </div>
        </div>
      )}

      {post.user_has_responded && (
        <p className="text-xs font-medium text-success-ink">✓ You answered this question</p>
      )}
    </div>
  );
};

// ── Announcement ────────────────────────────────────────────────────────────

const AnnouncementCard: React.FC<{ post: CommunityPost; onChange: (p: CommunityPost) => void }> = ({
  post,
  onChange,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const acknowledge = async () => {
    setBusy(true);
    setError("");
    try {
      onChange(await communityService.acknowledge(post.id));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not acknowledge.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {post.media_urls.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {post.media_urls.map((url, i) =>
            isImage(url) ? (
              <img key={i} src={url} alt="" className="w-full rounded-xl border border-border object-cover" />
            ) : isVideo(url) ? (
              <video key={i} src={url} controls className="w-full rounded-xl border border-border" />
            ) : (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="text-sm text-brand underline">
                Attachment {i + 1}
              </a>
            ),
          )}
        </div>
      )}
      {error && <p className="alert-error">{error}</p>}
      {post.user_has_responded ? (
        <p className="text-xs font-medium text-success-ink">✓ Acknowledged</p>
      ) : (
        <button type="button" onClick={acknowledge} disabled={busy} className="btn-primary btn-sm">
          {busy ? "Saving…" : "Acknowledge"}
        </button>
      )}
    </div>
  );
};

// ── Post wrapper ──────────────────────────────────────────────────────────────

const PostCard: React.FC<{ post: CommunityPost; onChange: (p: CommunityPost) => void }> = ({
  post,
  onChange,
}) => (
  <div id={`post-${post.id}`} className="card card-body space-y-3 scroll-mt-24">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className={typeBadge(post.post_type)}>{post.post_type}</span>
          {post.mandatory_response && <span className="badge-warning">Required</span>}
          {post.is_pinned && <span className="badge-neutral">📌 Pinned</span>}
        </div>
        <h3 className="text-base font-semibold text-foreground">{post.title}</h3>
        <p className="text-xs text-foreground-muted">
          {post.author_name} · {post.created_at ? formatIST(post.created_at) : ""}
        </p>
      </div>
    </div>

    {post.body && <p className="whitespace-pre-wrap text-sm text-foreground-secondary">{post.body}</p>}

    {post.post_type === "poll" && <PollCard post={post} onChange={onChange} />}
    {post.post_type === "question" && <QuestionCard post={post} onChange={onChange} />}
    {post.post_type === "announcement" && <AnnouncementCard post={post} onChange={onChange} />}
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const CommunityPage: React.FC = () => {
  const [tab, setTab] = useState("all");
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mandatory, setMandatory] = useState<CommunityPost[]>([]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = { limit: 50 };
      if (tab !== "all") params.post_type = tab;
      const res = await communityService.listPosts(params);
      // "Inactive" (admin-closed) posts are hidden from the user feed.
      setPosts(res.items.filter((p) => p.status !== "closed"));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load community posts.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const fetchMandatory = useCallback(async () => {
    try {
      const res = await communityService.mandatoryPending();
      setMandatory(res.items);
    } catch {
      setMandatory([]);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    fetchMandatory();
  }, [fetchMandatory]);

  const handleChange = (updated: CommunityPost) => {
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    // Once the user responds, drop it from the mandatory queue.
    if (updated.user_has_responded) {
      setMandatory((prev) => prev.filter((p) => p.id !== updated.id));
    }
  };

  const scrollToPost = (id: string) => {
    document.getElementById(`post-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="text-sm text-foreground-muted">Polls, questions & announcements for you.</p>
      </div>

      {mandatory.length > 0 && (
        <div className="alert-warning mb-5">
          <p className="font-semibold">
            You have {mandatory.length} required response{mandatory.length === 1 ? "" : "s"}.
          </p>
          <ul className="mt-2 space-y-1">
            {mandatory.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => scrollToPost(m.id)}
                  className="text-left text-sm underline underline-offset-2"
                >
                  {m.post_type === "poll" ? "📊" : m.post_type === "question" ? "❓" : "📢"} {m.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={tab === t.key ? "tab-active" : "tab"}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <span className="spinner" />
        </div>
      ) : error ? (
        <p className="alert-error">{error}</p>
      ) : posts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🗣️</div>
          <p className="empty-state-title">Nothing here yet</p>
          <p className="empty-state-desc">There are no community posts in this category right now.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onChange={handleChange} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
